import { useDisclosure } from "@mantine/hooks";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useParams } from "next/navigation";
import { useState } from "react";

import {
  formatBasicQuestionColumns,
  getColumnVisibilityState,
} from "@/components/screener-builder/review/tables/columns";
import { TableWrapper } from "@/components/screener-builder/review/tables/table-wrapper";
import { SortableTableRow } from "@/components/sortable-table-row";
import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import {
  QuestionType,
  type ReviewPageBasicQuestion,
} from "@/types/screener-builder";
import type { Maybe } from "@/types/utils";
import { buildTestId } from "@/utils/helpers";

/**
 * @deprecated Voice Phase 1 basic voice question table. New voice questions
 * use assessment-level responseFormat (Voice Phase 2). This component is
 * retained for displaying existing V1 voice questions in read-only mode.
 */
interface VoiceQuestionTableProps {
  onDeleteQuestion: (questionId: string, onSettled: () => void) => void;
  openQuestionModal: () => void;
  setSelectedQuestion: (
    question: ReviewPageBasicQuestion<QuestionType.Voice>,
  ) => void;
  voiceQuestions: ReviewPageBasicQuestion<QuestionType.Voice>[];
}

export const VoiceQuestionTable = ({
  onDeleteQuestion,
  openQuestionModal,
  setSelectedQuestion,
  voiceQuestions,
}: VoiceQuestionTableProps) => {
  const { id: screenerId } = useParams<{ id: string }>();
  const track = useTrackAnalytics({ screener_id: screenerId });
  const [
    warningModalOpened,
    { open: openWarningModal, close: closeWarningModal },
  ] = useDisclosure(false);
  const [deleteId, setDeleteId] = useState<Maybe<string>>();

  const columns = formatBasicQuestionColumns<QuestionType.Voice>(
    voiceQuestions,
    (question: ReviewPageBasicQuestion<QuestionType.Voice>) => {
      setSelectedQuestion(question);
      openQuestionModal();
    },
    (questionId: string) => {
      setDeleteId(questionId);
      openWarningModal();
    },
    // Voice rows are never locked
    () => false,
    true,
    track,
  );

  const table = useReactTable({
    columns,
    data: voiceQuestions,
    getCoreRowModel: getCoreRowModel(),
    // Tanstack will use indices by default. Use the question UUID instead to
    // keep a stable reference during drag and drop
    getRowId: ({ id }) => id,
    state: {
      columnVisibility: getColumnVisibilityState(true),
    },
  });

  return (
    <TableWrapper
      addQuestionDisabled={voiceQuestions.length > 0}
      addQuestionTestId={buildTestId(
        "builder-review",
        "voice-questions",
        "add-question",
      )}
      // AS-2019: multilingual no longer blocks voice, so the one-voice-question
      // limit of this split-section layout is the only reason left.
      addQuestionTooltip="Interview can only include one voice question"
      addQuestionTooltipDisabled={voiceQuestions.length < 1}
      closeWarningModal={closeWarningModal}
      confirmDelete={() => {
        if (deleteId) {
          onDeleteQuestion(deleteId, () => {
            closeWarningModal();
            setDeleteId(undefined);
          });
        }
      }}
      header="Voice question"
      isReadOnly
      onAddQuestionClick={() => {
        track("ADD VOICE QUESTION + Button Clicked");
        openQuestionModal();
      }}
      tableHeaders={table.getFlatHeaders()}
      infoTooltipLabel="Question that will require candidates to answer with a recorded voice snippet"
      warningModalOpened={warningModalOpened}
    >
      {/* Don't render an empty table if there are no questions */}
      {voiceQuestions.length > 0 &&
        table
          .getRowModel()
          .rows.map((row, index) => (
            <SortableTableRow
              cellTDClassName="min-h-14 px-4 py-2"
              className={[
                "bg-white",
                "border-gray-200",
                "border-t",
                "min-h-14",
                "p-0",
              ]}
              disabled
              id={row.original.id}
              index={index}
              isVoice
              key={row.id}
              renderCell={(cell) =>
                flexRender(cell.column.columnDef.cell, cell.getContext())
              }
              row={row}
            />
          ))}
    </TableWrapper>
  );
};
